import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { ConsumptionGraph, ConsumptionHistoryPayload, ConsumptionReport, IConsumptionsPayload } from "@edp-models/consumptions.model";
import { EdpResponseBody } from "@edp-models/response.model";
import { GetMobieConsumptionsPayload, GetStandardConsumptionEvolutionPayload } from "@edp-models/smart-now.model";
import { ApiService } from "@edp-services/api.service";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    ActiveMeters,
    ActiveMetersFactory,
    LastInserted,
    LastInsertedFactory,
    LastValidReading,
    LastValidReadingFactory,
    Meters,
    MetersFactory,
    Simulation,
    SimulationFactory,
} from "../@components/edp-card-widgets/edp-widget-consumptions/edp-consumptions.models";

@Injectable({
    providedIn: "root",
})
export class ConsumptionsService {
    constructor(private apiService: ApiService ,private store: Store<IAppState>, private router: Router) {}

    public getContract = (): Observable<Contract> => {
        return this.store.pipe(select(getContract));
    };

    public getActiveMeters(contract: Contract, supplyType: string = "", isLocal?: boolean) {
        return this.apiService.callServicePostToCollection<ActiveMeters>("reading.getActiveMeters", {
            contractId: contract.ContractId,
            SupplyType: !!supplyType ? supplyType : this.getSupplyType(contract),
            isLocal: isLocal == null ? true : isLocal,
        });
    }

    public createActiveMeters(activeMeters: ActiveMeters): ActiveMeters {
        let meters: Meters[] = [];
        if (activeMeters.meters) {
            meters = activeMeters.meters.map(meter => {
                return MetersFactory.create({
                    MinRead: meter.MinRead,
                    MaxRead: meter.MaxRead,
                    RegisterType: meter.RegisterType,
                    PrevMrType: meter.PrevMrType,
                    Brand: meter.Brand,
                    Material: meter.Material,
                    Model: meter.Model,
                    Serialno: meter.Serialno,
                    Register: meter.Register,
                    RegisterTypeStr: meter.RegisterTypeStr,
                    ReadingResult: meter.ReadingResult,
                    OutsideConsumption: meter.OutsideConsumption,
                    Predecimalplaces: meter.Predecimalplaces,
                } as Meters);
            });
        }

        let lastInserted: LastInserted;
        if (activeMeters.lastInserted) {
            lastInserted = LastInsertedFactory.create({
                date: activeMeters.lastInserted.date,
                mr_type: activeMeters.lastInserted.mr_type,
                S: activeMeters.lastInserted.S,
                FV: activeMeters.lastInserted.FV,
                V: activeMeters.lastInserted.V,
                C: activeMeters.lastInserted.C,
                P: activeMeters.lastInserted.P,
                LT: activeMeters.lastInserted.LT,
                telemetry: activeMeters.lastInserted.telemetry,
                collectReadingType: activeMeters.lastInserted.collectReadingType,
            } as LastInserted);
        }

        let lastValidReading: LastValidReading;
        if (activeMeters.lastValidReading) {
            lastValidReading = LastValidReadingFactory.create({
                date: activeMeters.lastValidReading.date,
                mr_type: activeMeters.lastValidReading.mr_type,
                S: activeMeters.lastValidReading.S,
                FV: activeMeters.lastValidReading.FV,
                V: activeMeters.lastValidReading.V,
                C: activeMeters.lastValidReading.C,
                P: activeMeters.lastValidReading.P,
                LT: activeMeters.lastValidReading.LT,
                telemetry: activeMeters.lastValidReading.telemetry,
                collectReadingType: activeMeters.lastValidReading.collectReadingType,
            } as LastValidReading);
        }

        let simulation: Simulation;
        if (activeMeters.simulation) {
            simulation = this.createSimulation(activeMeters.simulation);
        }

        return ActiveMetersFactory.create({
            meters,
            lastInserted,
            lastValidReading,
            simulation,
            supplyType: activeMeters.supplyType,
            idMeter: activeMeters.idMeter,
            selfConsumption: activeMeters.selfConsumption,
        } as ActiveMeters);
    }

    public uploadReading(args: any) {
        return this.apiService.callServicePost("reading.uploadReading", args).pipe(
            map((response: EdpResponseBody) => {
                return response.Result.Collection;
            })
        );
    }

    public getSimulation(contractId: string, offline: boolean) {
        const body = {
            contractId,
            offline,
        };
        return this.apiService.callServicePostToCollection("reading.getSimulation", body);
    }

    public createSimulation(simulation: Simulation): Simulation {
        return SimulationFactory.create({
            simulationValue: simulation.simulationValue,
            startBillingDate: simulation.startBillingDate,
            endBillingDate: simulation.endBillingDate,
            readingDate: simulation.readingDate,
            electricity: simulation.electricity,
            gas: simulation.gas,
            services: simulation.services,
            taxes: simulation.taxes,
            estimatedDays: simulation.estimatedDays,
            realDays: simulation.realDays,
        } as Simulation);
    }

    public getMeterInstructions() {
        return this.apiService.callServicePostToCollection("reading.getMeterInstructions", []);
    }

    public getConsumptionsChartData(args: IConsumptionsPayload) {
        return this.apiService.callServicePostToCollection("consumption.getGraphData", args);
    }

    public getConsumptionPeriod(args: IConsumptionsPayload) {
        return this.apiService.callServicePostToCollection("consumption.getCurrentPeriodConsumption", args);
    }

    public getConsumptionEvolution(args: GetStandardConsumptionEvolutionPayload) {
        return this.apiService.callServicePostToCollection("consumption.getConsumptionGraph", args);
    }

    public getConsumptionHistory(args: ConsumptionHistoryPayload) {
        return this.apiService.callServicePostToCollection("consumption.getConsumptionHistory", args);
    }

    public getMobieConsumptionHistory(args: ConsumptionHistoryPayload) {
        return this.apiService.callServicePostToCollection("consumption.getMobieConsumptionHistory", args);
    }

    public getConsumptionReportDownload(args: ConsumptionReport) {
        return this.apiService.callServicePostCatalog('report.generate', args);
    }

    public getMobieCardList(args: IConsumptionsPayload) {
        return this.apiService.callServicePostToCollection("consumption.getMobieCardList", args);
    }

    public getElectricMobilityWidgetData(contractId: string) {
        return this.apiService.callServicePostToCollection("consumption.getMobieResumedData", { contractId });
    }
    public getMobieConsumptions(args: GetMobieConsumptionsPayload) {
        return this.apiService.callServicePostToCollection("consumption.getMobieGraphData", args);
    }

    public getSupplyType(contract) {
        const type = contract.getType();
        if (type === "electricity" || type === "dual") {
            return "E";
        } else if (type === "gas") {
            return "G";
        }

        return null;
    }

    public getGraph(args: ConsumptionGraph) {
        return this.apiService.callServicePostToCollectionCatalog("smartNetwork.getGraph", args);
    }

    public getGraphAvarage(args: ConsumptionGraph) {
        return this.apiService.callServicePostToCollectionCatalog("smartNetwork.getGraphAvg", args);
    }
}
